import { api } from 'careand-redux/api/ApiThunks';
import {
  ActionResult,
  actionsActions,
  ActionType,
  ClinicalAction,
} from 'careand-redux/slices/actions';
import { store } from 'careand-redux/store';
import { UnwrappedAppDispatch } from 'careand-redux/useAppDispatch';
import AIUtils from './AIUtils';
import CPPUtils from './CPPUtils';
import moment from 'moment-timezone';
import _ from 'lodash';
import type { PrescriptionMedication } from '../generated/api';

type ExecutorFunction<T extends ActionType> = (
  action: ClinicalAction<T>,
) => Promise<ActionResult[T]>;

export class ActionExecutor {
  private dispatch: UnwrappedAppDispatch;

  constructor(dispatch: UnwrappedAppDispatch) {
    this.dispatch = dispatch;
  }

  async execute(action: ClinicalAction<any>): Promise<void> {
    // Mark as in-progress
    action = { ...action, status: 'in-progress', updated_at: Date.now() };
    this.dispatch(actionsActions.updateAction(action));

    try {
      let executionData: any;

      switch (action.type) {
        case 'prescription':
          executionData = await this.executePrescription(action);
          break;
        case 'requisition':
        case 'referral':
        case 'other_document':
          executionData = await this.executeDocumentSearchAndFill(action);
          break;
        case 'other':
          executionData = { note: action.description };
          break;
      }

      // Mark as pending approval with execution data
      this.dispatch(
        actionsActions.updateAction({
          ...action,
          status: 'pending-approval',
          execution_data: executionData,
          updated_at: Date.now(),
        }),
      );
    } catch (error: any) {
      // Mark as error
      this.dispatch(
        actionsActions.updateAction({
          ...action,
          status: 'error',
          error_message: error.message,
          updated_at: Date.now(),
        }),
      );
    }
  }

  private executeDocumentSearchAndFill: ExecutorFunction<
    'referral' | 'requisition' | 'other_document'
  > = async (action) => {
    const state = store.getState();
    const bubble = state.ui.bubbles.find((it) => it.appointment_id === action.appointment_id);
    const appointment = bubble?.appointment;

    if (!appointment?.patient_id) {
      throw new Error('Missing appointment or patient_id');
    }

    const patientId = appointment.patient_id;

    // Step 1: Update status - searching for template
    this.dispatch(
      actionsActions.updateAction({
        ...action,
        executor_message: 'Searching for document template...',
        updated_at: Date.now(),
      }),
    );

    // Step 2: Get context data
    let transcription = '';
    if (action.appointment_id) {
      const res = await this.dispatch(
        api.Appointments.getAppointmentTranscription({ id: action.appointment_id }),
      );
      const text = res?.messages?.map((msg) => `${msg.from}: ${msg.text}`)?.join('\n');
      if (text && text.length > 0) {
        transcription = text;
      }
    }

    const drafts = (await this.dispatch(api.HealthRecords.getOwnDrafts())) ?? [];
    const appointmentNote = _.orderBy(drafts, ['date'], ['desc'])[0]?.note.value.value ?? '';

    // Step 3: Get previous templates for context
    const previousTemplates = _(state.documents.byId[patientId])
      .orderBy(['date'], ['desc'])
      .map((doc) => {
        if (!doc.template_id) return;
        const template = state.templates.templates.find((it) => it.id === doc.template_id);
        if (!template?.active) return;
        return `{ id: ${template.id}, name: ${template.description} }`;
      })
      .compact()
      .uniqBy('id')
      .value()
      .join(';\n');

    // Step 4: Generate template suggestions
    const suggestionsResult = await this.dispatch(
      api.AI.runSavedPrompt({
        name: 'Generate Document Template Suggestions',
        savedPromptRequest: {
          patient_id: patientId,
          fields: [
            { name: 'note', value: appointmentNote },
            { name: 'appointment', value: JSON.stringify(appointment, null, 2) },
            { name: 'appointmentTranscription', value: transcription || 'No Transcription' },
            { name: 'previousDocuments', value: previousTemplates || 'No previous Documents' },
            { name: 'actionDescription', value: action.description },
          ],
        },
      }),
    );

    // Parse suggested template IDs from AI response
    let suggestedTemplateIds: string[] = [];
    if (suggestionsResult?.value) {
      const result = AIUtils.findJSONSection(
        suggestionsResult.value,
        (it: unknown) =>
          it &&
          typeof it === 'object' &&
          'type' in it &&
          typeof it.type === 'string' &&
          it.type === 'tool_use' &&
          'name' in it &&
          typeof it.name === 'string' &&
          it.name === 'submit',
      );
      if (result) {
        suggestedTemplateIds = result.input.result.suggestedTemplateIds;
      }
    }

    if (!suggestedTemplateIds || suggestedTemplateIds.length === 0) {
      throw new Error('No suitable document templates found');
    }

    // Step 5: Get the best template
    const templateId = suggestedTemplateIds[0];
    const templates = state.templates.templates;
    const template = templates.find((it) => it.id === templateId);

    if (!template || !template.id) {
      throw new Error(`Template ${templateId} not found`);
    }

    // Step 6: Update status - auto-filling template
    this.dispatch(
      actionsActions.updateAction({
        ...action,
        executor_message: `Auto-filling ${template.description || 'template'}...`,
        updated_at: Date.now(),
      }),
    );

    // Step 7: Auto-fill the template
    const filledFields = await this.dispatch(
      api.Templates.fillDocumentTemplate({
        templateId: template.id,
        fillDocumentTemplateParams: {
          appointment,
          patient_id: patientId,
          appointment_note: appointmentNote,
          extra_instructions: action.description,
        },
      }),
    );

    // Step 8: Return the result
    return {
      templateId: template.id!,
      templateDescription: template.description || '',
      fields: filledFields ?? [],
    };
  };

  private executePrescription: ExecutorFunction<'prescription'> = async (action) => {
    // Get appointment and patient context
    const state = store.getState();
    const bubble = state.ui.bubbles.find((it) => it.appointment_id === action.appointment_id);
    const appointment = bubble?.appointment;

    if (!appointment?.patient_id) {
      throw new Error('Missing appointment or patient_id');
    }

    const patientId = appointment.patient_id;
    const patientData = state.healthRecords.byId[patientId];
    const prescriptions = patientData?.prescriptions;
    const favoriteMedications = state.drugs.favoriteMedications;
    const allergies =
      patientData?.healthRecords?.allergies
        ?.filter((allergy) => CPPUtils.isHealthRecordItemEnabled(allergy))
        ?.map((allergy) => allergy.value.name)
        ?.join(', ') || 'NKA';

    // Get transcription
    let transcription = '';
    if (action.appointment_id) {
      const res = await this.dispatch(
        api.Appointments.getAppointmentTranscription({ id: action.appointment_id }),
      );
      const text = res?.messages?.map((msg) => `${msg.from}: ${msg.text}`)?.join('\n');
      if (text && text.length > 0) {
        transcription = text;
      }
    }

    // Get appointment note draft
    const drafts = (await this.dispatch(api.HealthRecords.getOwnDrafts())) ?? [];
    const draft = _.orderBy(drafts, ['date'], ['desc'])[0]?.note.value.value ?? '';

    // Format recent prescriptions
    const prettyPrescriptions = JSON.stringify(
      _.orderBy(prescriptions ?? [], ['date'], ['desc'])
        .slice(0, 15)
        .map((it) => ({
          ...it,
          date: moment.unix(it.date).fromNow(),
        })),
      null,
      2,
    );

    // Call the Generate Prescription prompt (same as useGeneratePrescription)
    const result = await this.dispatch(
      api.AI.runSavedPrompt({
        name: 'Generate Prescription',
        savedPromptRequest: {
          patient_id: patientId,
          fields: [
            { name: 'note', value: draft },
            { name: 'appointment', value: JSON.stringify(appointment, null, 2) },
            { name: 'transcription', value: transcription },
            { name: 'prescriptions', value: prettyPrescriptions },
            { name: 'allergies', value: allergies },
            {
              name: 'favoriteMedications',
              value: JSON.stringify(_.compact(favoriteMedications.map((it) => it.value)), null, 2),
            },
            { name: 'additional_context', value: action.description }, // Add the AI suggestion as context
          ],
        },
      }),
    );

    // Parse medication from streaming response (tool_use blocks)
    let medication: PrescriptionMedication | undefined = undefined;
    if (result?.value) {
      //@ts-expect-error forEach exists in Generator prototype chain
      AIUtils.generateJSONSections(result.value).forEach((tool: any) => {
        if (tool?.type === 'tool_use' && tool.name === 'add_medication') {
          medication = tool.input.medication as PrescriptionMedication;
        }
      });
    }

    return { medication };
  };
}
